<template>
  <div class="guest-checkout">
    <el-form :model="form" :rules="rules" ref="guestForm" label-position="top">
      <h3 class="section-title">{{ $t('message.home.contactInfo') || 'Contact Information' }}</h3>

      <el-form-item :label="$t('message.home.email') || 'Email'" prop="email">
        <el-input v-model="form.email" placeholder="example@email.com" @blur="onEmailBlur"></el-input>
        <p class="email-hint" v-if="emailExists">
          {{ $t('message.home.emailRegistered') || 'This email is already registered. You can login after checkout.' }}
        </p>
      </el-form-item>

      <h3 class="section-title">{{ $t('message.home.shippingAddress') || 'Shipping Address' }}</h3>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item :label="$t('message.home.consignee') || 'Full Name'" prop="consignee">
            <el-input v-model="form.consignee" :placeholder="$t('message.home.enterName') || 'Enter full name'"></el-input>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item :label="$t('message.home.phone') || 'Phone'" prop="phone">
            <div class="form-phone">
              <el-input v-model="form.phone" :placeholder="$t('message.home.enterPhone') || 'Enter phone number'" :maxlength="20"></el-input>
              <VueCountryIntl
                v-model="form.areaCode"
                schema="popover"
                :searchInputPlaceholder="$t('message.home.pleaseSetAreaCode') || 'Search area code'"
                noDataText="No data found"
              >
                <div class="area-code flex-between" slot="reference">
                  <span>+{{ form.areaCode || '' }}</span>
                  <i class="el-icon-caret-bottom"></i>
                </div>
              </VueCountryIntl>
            </div>
          </el-form-item>
        </el-col>
      </el-row>

      <div class="check_address">
        <el-form-item :label="$t('message.home.country') || 'Country'" prop="regionId">
          <el-select v-model="currentCountry" :placeholder="$t('message.home.nation') || 'Country'" filterable>
            <el-option
              v-for="(item, index) in countryList"
              :key="item.id"
              :value="item.id"
              :label="item.name"
              @click.native="countryListChange(index)"
            >
              <span>{{ item.name }}</span>
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('message.home.province') || 'State/Province'">
          <el-select v-model="currentProvince" :placeholder="$t('message.home.state') || 'State/Province'" filterable>
            <el-option
              v-for="(item, index) in provinces"
              :key="item.id"
              :label="item.name"
              :value="item.id"
              @click.native="provinceListChange(index)"
            >
              <span>{{ item.name }}</span>
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('message.home.city') || 'City'">
          <el-select v-model="currentCity" :placeholder="$t('message.home.city') || 'City'" filterable>
            <el-option
              v-for="(item, index) in cities"
              :key="item.id"
              :label="item.name"
              :value="item.id"
              @click.native="cityListChange(index)"
            >
              <span>{{ item.name }}</span>
            </el-option>
          </el-select>
        </el-form-item>
      </div>

      <el-form-item :label="$t('message.home.postCode') || 'Zip Code'">
        <el-input v-model="form.zipCode" :placeholder="$t('message.home.postCode') || 'Zip Code'" :maxlength="9"></el-input>
      </el-form-item>

      <el-form-item :label="$t('message.home.detailAddress') || 'Detail Address'" prop="address">
        <el-input v-model="form.address" type="textarea" :rows="2"
          :placeholder="$t('message.home.enterAddress') || 'Street address, apartment, suite, etc.'"></el-input>
      </el-form-item>

      <el-form-item class="submit-form-item">
        <el-button type="primary" class="submit-btn" :loading="loading" @click="submitGuest">
          {{ $t('message.home.subOrder') || 'Submit Order' }}
        </el-button>
      </el-form-item>

    </el-form>

    <el-dialog :visible.sync="payModalShow" class="es-dialog" :append-to-body="true" width="475px">
      <div slot="title" class="dialog-title">
        <span>{{ $t('message.home.confirm') || 'Confirm' }}</span>
      </div>
      <div class="pay-modal-content dialog-content">
        <h2 style="width: 100%; line-height: 120px; text-align: center;" class="pay-modal-title">
          {{ $t('message.home.confirmPayOrder') || 'Confirm payment order?' }}
        </h2>
        <p style="text-align: center; font-size: 24px; color: #409EFF; margin-bottom: 20px;">
          ${{ orderAmount || '0' }}
        </p>
        <div class="pay-modal-btn">
          <el-button type="primary" :loading="payLoading" @click="payWithPayPal">
            {{ $t('message.home.confirm') || 'Confirm' }}
          </el-button>
        </div>
      </div>
      <span slot="footer"></span>
    </el-dialog>
  </div>
</template>

<script>
import { guestRegister, checkEmail } from '@/api/upms/guest'
import { add } from '@/api/upms/address'
import { saveGuestLoginInfo, getGuestCart, clearGuestCart } from '@/util/guestCart'
import { loginByUsername } from '@/api/login'
import { encryption } from '@/util/util'
import { securityKey } from '@/config/index'
import { BIZ_TOKEN, BIZ_TOKEN_EXPIRE, BIZ_TOKEN_EXPIRE_MS, BASE_AREA_CODE } from '@/common/constant'
import { checkout, create, add_address } from '@/api/order/order'
import { load, pay, is_pay_success } from '@/api/order/payment'
import VueCountryIntl from 'vue-country-intl'
import 'vue-country-intl/lib/vue-country-intl.css'

export default {
  name: 'GuestCheckout',
  components: { VueCountryIntl },
  data() {
    return {
      loading: false,
      emailExists: false,
      countryList: [],
      provinces: [],
      cities: [],
      currentCountry: '',
      currentProvince: '',
      currentCity: '',
      payModalShow: false,
      payLoading: false,
      orderAmount: '0',
      currentOrderSns: null,
      currentPaymentLogSn: null,
      checkoutData: null,
      savedAddressId: null,
      form: {
        email: '',
        consignee: '',
        phone: '',
        areaCode: BASE_AREA_CODE,
        regionId: '',
        regionName: '',
        zipCode: '',
        address: ''
      },
      rules: {
        email: [
          { required: true, message: this.$t('message.home.emailRequired') || 'Email is required', trigger: 'blur' },
          { type: 'email', message: this.$t('message.home.emailInvalid') || 'Invalid email format', trigger: 'blur' }
        ],
        consignee: [
          { required: true, message: this.$t('message.home.nameRequired') || 'Name is required', trigger: 'blur' }
        ],
        phone: [
          { required: true, message: this.$t('message.home.phoneRequired') || 'Phone is required', trigger: 'blur' }
        ],
        regionId: [
          { required: true, message: this.$t('message.home.countryRequired') || 'Country is required', trigger: 'change' }
        ],
        address: [
          { required: true, message: this.$t('message.home.addressRequired') || 'Address is required', trigger: 'blur' }
        ]
      }
    }
  },
  created() {
    this.loadRegions()
  },
  methods: {
    loadRegions() {
      add().then(response => {
        const regions = response.data.data.regions
        this.countryList = regions.filter(r => r.level === 0)
      }).catch(e => {
        console.warn('Failed to load regions', e)
      })
    },

    countryListChange(index) {
      this.provinces = this.countryList[index].children || []
      this.cities = []
      this.currentProvince = ''
      this.currentCity = ''
      this.form.regionId = this.countryList[index].id
      this.form.regionName = this.countryList[index].name
    },

    provinceListChange(index) {
      this.cities = this.provinces[index].children || []
      this.currentCity = ''
      this.form.regionId = this.provinces[index].id
      this.form.regionName = this.provinces[index].name
    },

    cityListChange(index) {
      this.form.regionId = this.cities[index].id
      this.form.regionName = this.cities[index].name
    },

    async onEmailBlur() {
      if (!this.form.email) return
      try {
        const res = await checkEmail({ email: this.form.email })
        this.emailExists = res && res.data && res.data.exists
      } catch (e) {
        // ignore
      }
    },

    submitGuest() {
      this.$refs.guestForm.validate(async (valid) => {
        if (!valid) return
        this.loading = true
        try {
          const submitData = {
            email: this.form.email,
            consignee: this.form.consignee,
            phone: this.form.areaCode + ' ' + this.form.phone,
            areaCode: this.form.areaCode,
            regionId: this.form.regionId,
            regionName: this.form.regionName,
            zipCode: this.form.zipCode,
            address: this.form.address
          }

          const res = await guestRegister(submitData)
          if (!res || !res.data) {
            this.$message.error(this.$t('message.home.registerFailed') || 'Registration failed')
            return
          }
          const data = res.data

          // 保存游客信息（15天有效）
          saveGuestLoginInfo(data)

          if (data.exists) {
            this.$message.info(this.$t('message.home.accountExists') || 'Account already exists, please login to continue')
            this.$emit('needLogin', { username: data.username })
            return
          }

          // 新注册成功，自动登录
          try {
            const user = encryption({
              data: { username: data.username, password: data.password },
              key: securityKey,
              param: ['password']
            })
            const loginRes = await loginByUsername(user.username, user.password, '', '')
            if (loginRes && loginRes.data && loginRes.data.access_token) {
              localStorage.setItem(BIZ_TOKEN, loginRes.data.access_token)
              localStorage.setItem(BIZ_TOKEN_EXPIRE, String(Date.now() + BIZ_TOKEN_EXPIRE_MS))
              if (loginRes.data.user) {
                this.$store.commit('SETUSERINFO', loginRes.data.user)
              }
              this.$store.commit('SETHANDERS', {
                'authorization': 'Bearer ' + loginRes.data.access_token,
                'userId': loginRes.data.user_id || (loginRes.data.user ? loginRes.data.user.id : data.memberId),
                'username': data.username
              })
              this.$store.commit('SETTOKEN', loginRes.data.access_token)
            }
          } catch (loginErr) {
            console.warn('Auto login failed, continuing with checkout:', loginErr)
          }

          // 保存收货地址
          try {
            const addressData = {
              consignee: this.form.consignee,
              phone: this.form.phone,
              areaCode: this.form.areaCode,
              regionId: this.form.regionId,
              regionName: this.form.regionName,
              zipCode: this.form.zipCode,
              address: this.form.address,
              isDefault: true
            }
            const addressRes = await add_address(addressData)
            if (addressRes && addressRes.data && addressRes.data.data) {
              this.savedAddressId = addressRes.data.data.id
            }
          } catch (addressErr) {
            console.warn('Save address failed, will try without address:', addressErr)
          }

          // 同步游客购物车到服务器
          const guestItems = getGuestCart()
          const primaryGuestItem = guestItems && guestItems.length > 0 ? guestItems[0] : null
          const routeSkuId = this.$route?.query?.skuId || null
          const routeQuantity = this.$route?.query?.quantity || null
          const skuId = primaryGuestItem ? primaryGuestItem.skuId : routeSkuId
          const quantity = primaryGuestItem ? primaryGuestItem.quantity : routeQuantity
          let orderSns = []
          let paymentPatternId = null
          let shippingMethodId = null
          if (guestItems && guestItems.length > 0) {
            const { add: cartAdd, get_current } = await import('@/api/order/cart.js')
            for (const item of guestItems) {
              try {
                await cartAdd({
                  skuId: item.skuId,
                  quantity: item.quantity
                })
              } catch (e) {
                console.warn('Sync guest cart item failed:', item.skuId, e)
              }
            }
            clearGuestCart()
            try {
              const cartRes = await get_current({})
              if (cartRes && cartRes.data && cartRes.data.data && cartRes.data.data.currentCart) {
                this.$store.commit('SETCURRENTCART', cartRes.data.data.currentCart)
              }
            } catch (cartErr) {
              console.warn('Get current cart failed:', cartErr)
            }
          }

          if (!skuId && !this.$store.state.currentCart?.id) {
            this.$message.error(this.$t('message.home.noCartTips') || 'Your shopping cart is empty. Please re-enter from the product page.')
            this.loading = false
            return
          }

          const checkoutParams = skuId
            ? { skuId, quantity, notJoinGroup: false }
            : { currentCartId: this.$store.state.currentCart?.id }
          if (!checkoutParams.skuId && !checkoutParams.currentCartId) {
            this.$message.error(this.$t('message.home.noCartTips') || 'Your shopping cart is empty. Please re-enter from the product page.')
            this.loading = false
            return
          }
          try {
            const checkoutRes = await checkout(checkoutParams)
            if (checkoutRes && checkoutRes.data && checkoutRes.data.data) {
              this.checkoutData = checkoutRes.data.data
              this.orderAmount = checkoutRes.data.data.amountPayable || checkoutRes.data.data.amount || '0'
              const paymentPatterns = checkoutRes.data.data.paymentPatterns
              const shippingMethods = checkoutRes.data.data.shippingMethods
              if (paymentPatterns && paymentPatterns.length > 0) {
                const paypalPattern = paymentPatterns.find(p => p.id === 'paypalPayment')
                paymentPatternId = paypalPattern ? paypalPattern.id : paymentPatterns[0].id
              }
              if (shippingMethods && shippingMethods.length > 0) {
                shippingMethodId = shippingMethods[0].id
              }
            }
          } catch (checkoutErr) {
            console.warn('Checkout error, will try create directly:', checkoutErr)
          }

          try {
            const createData = skuId
              ? {
                skuId,
                quantity,
                addressId: this.savedAddressId,
                paymentPatternId: paymentPatternId,
                shippingMethodId: shippingMethodId
              }
              : {
                currentCartId: this.$store.state.currentCart?.id,
                addressId: this.savedAddressId,
                paymentPatternId: paymentPatternId,
                shippingMethodId: shippingMethodId
              }
            const createRes = await create(createData)
            if (createRes && createRes.data && createRes.data.data) {
              const createDataResult = createRes.data.data
              orderSns = createDataResult.orderSns || []
              this.currentOrderSns = orderSns
              if (orderSns && orderSns.length > 0) {
                this.payWithPayPal()
              } else {
                this.$message.warning(this.$t('message.home.noItemsToCheckout') || 'No items to checkout')
              }
            } else {
              this.$message.warning(this.$t('message.home.createOrderFailed') || 'Create order failed')
            }
          } catch (createErr) {
            console.error('Create order error:', createErr)
            this.$message.error(createErr.message || this.$t('message.home.createOrderFailed') || 'Create order failed')
          }

          if (orderSns && orderSns.length === 0) {
            this.$emit('success', data)
          }

        } catch (error) {
          console.error('Guest register error:', error)
          this.$message.error(error.message || this.$t('message.home.operationFailed') || 'Operation failed')
        } finally {
          this.loading = false
        }
      })
    },

    async payWithPayPal() {
      if (!this.currentOrderSns || this.currentOrderSns.length === 0) {
        this.$message.error(this.$t('message.home.noOrderToPay') || 'No order to pay')
        return
      }
      this.payLoading = true
      try {
        const paymentItemList = this.currentOrderSns.map(orderSn => ({
          orderSn: orderSn,
          type: 'ORDER_PAYMENT'
        }))
        const loadRes = await load({ paymentItemList }, {
          paymentId: process.env.VUE_APP_PAYMENT_PROVIDER || 'stripePayment',
          rePayUrl: '/paySuccess'
        })
        const paymentLog = loadRes?.data?.data?.paymentLog || loadRes?.data?.paymentLog
        if (!paymentLog || !paymentLog.sn) {
          throw new Error(this.$t('message.home.createPaymentFailed') || 'Failed to create payment')
        }
        const paymentLogSn = paymentLog.sn
        this.currentPaymentLogSn = paymentLogSn
        const payRes = await pay({
          paymentLogSn: paymentLogSn,
          extra: window.location.origin,
        })
        if (!payRes || !payRes.data) {
          throw new Error(this.$t('message.home.payFailed') || 'Failed to initiate payment')
        }
        const resultBody = payRes?.data?.data?.resultBody
        const paypalOrderId = payRes?.data?.data?.resultOrderId
        if (resultBody) {
          localStorage.setItem('paypalPaymentLogSn', paymentLogSn)
          localStorage.setItem('paypalOrderId', paypalOrderId)
          window.location.href = resultBody
        } else {
          throw new Error(this.$t('message.home.noPayUrl') || 'No payment URL returned')
        }
      } catch (error) {
        console.error('PayPal payment error:', error)
        this.$message.error(error.message || this.$t('message.home.paymentFailed') || 'Payment failed')
      } finally {
        this.payLoading = false
      }
    }
  }
}
</script>

<style lang="scss">
.vue-country-item.selected .selected-text {
  display: none;
}
</style>

<style scoped lang="scss">
.guest-checkout {
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  max-width: 700px;

  .section-title {
    font-size: 16px;
    font-weight: 600;
    margin: 20px 0 15px;
    padding-bottom: 10px;
    border-bottom: 1px solid #eee;

    &:first-child {
      margin-top: 0;
    }
  }

  .email-hint {
    color: #e6a23c;
    font-size: 12px;
    margin-top: 4px;
  }

  .check_address {
    display: flex;
    gap: 10px;
    margin-bottom: 10px;

    .el-form-item {
      flex: 1;
    }
  }

  .form-phone {
    position: relative;
    cursor: pointer;

    ::v-deep .el-input__inner {
      padding-left: 100px;
    }

    ::v-deep .vue-country-popover-container {
      position: absolute;
      top: 50%;
      left: 0;
      transform: translateY(-50%);
    }

    .area-code {
      width: 80px;

      span {
        display: inline-block;
        width: 50px;
        padding-left: 8px;
      }
    }
  }

  .submit-btn {
    width: 100%;
    height: 44px;
    font-size: 16px;
    margin-top: 10px;
  }
}
</style>
